/**
 * InstancedStocker.tsx
 *
 * High-performance GPU Instanced rendering for all Fab Stockers.
 * Replaces low-detail shell boxes with the REAL, HIGH-QUALITY 3D stocker models:
 * - Stocker3D (High-capacity cleanroom automated storage & retrieval stocker)
 * - CircleStocker (Cylindrical rotary high-density wafer stocker)
 *
 * Decomposes the full multi-part stocker assemblies (pillars, brushed metal frames,
 * transparent cyan-blue glass panels, internal shelves, trays, and load port banks)
 * into constituent sub-mesh parts, rendered via GPU InstancedMesh in 1 draw call per part.
 */

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useAppSelector } from '../../hooks/redux';
import type { EquipmentRenderDescriptor } from '../../components/canvas/equipmentSilhouettePorts';
import { resolveKitBox } from '../../components/canvas/equipmentSilhouettePorts';
import { getEquipmentKitWorldTransform } from '../../components/canvas/fabKitWorldTransform';
import type { LayoutElement } from '../../types';
import {
  instancedAssetManager,
  InstancedMultiMeshGroup,
  type InstanceTransform,
  type SubMeshPart,
} from './InstancedAssetManager';
import {
  getSharedStandardMaterial,
  getIndustrialDarkChassisMaterial,
  getIndustrialWhiteMaterial,
  getBrushedAluminumMaterial,
  getPolishedChromeMaterial,
  getCleanroomGlassMaterial,
} from '../../utils/threeResourcePool';
import { AMHS_KIT_TEMPLATE_DIMS } from './amhsKitTemplateDims';
import { getAmhsKitTemplateParts } from './amhsKitTemplateStore';
import { useAmhsKitTemplateRevision } from './useAmhsKitTemplateRevision';

// ============================================================================
// TEMPLATE DIMS & BUILDERS
// ============================================================================

const STOCKER_TEMPLATE_DIMS = AMHS_KIT_TEMPLATE_DIMS.Stocker3D;
const CIRCLE_STOCKER_TEMPLATE_DIMS = AMHS_KIT_TEMPLATE_DIMS.CircleStocker;

let cachedOledTexture: THREE.CanvasTexture | null = null;

function getStockerOledTexture(): THREE.CanvasTexture | null {
  if (cachedOledTexture) return cachedOledTexture;
  if (typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Dark Glossy Obsidian Faceplate Background
    ctx.fillStyle = '#060C18';
    ctx.fillRect(0, 0, 512, 256);

    // Glowing Cyan Border Lip
    ctx.strokeStyle = '#00F0FF';
    ctx.lineWidth = 10;
    ctx.strokeRect(6, 6, 500, 244);

    // Inner Frame
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 3;
    ctx.strokeRect(16, 16, 480, 224);

    // Top Status Pill
    ctx.fillStyle = '#10B981';
    if (typeof (ctx as any).roundRect === 'function') {
      (ctx as any).roundRect(26, 26, 110, 42, 10);
    } else {
      ctx.rect(26, 26, 110, 42);
    }
    ctx.fill();

    ctx.fillStyle = '#022C22';
    ctx.font = 'bold 22px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('● STK', 81, 47);

    // Top Stocker Name
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 26px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('STOCKER A-01', 152, 47);

    // Middle Glowing Seam Line
    ctx.fillStyle = '#00F0FF';
    ctx.fillRect(26, 82, 460, 4);

    // Bottom Big Count Display: "0/28"
    ctx.font = '900 95px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#00F0FF';
    ctx.fillText('0', 255, 168);

    ctx.textAlign = 'left';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText('/28', 265, 168);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    cachedOledTexture = tex;
    return tex;
  } catch {
    return null;
  }
}

/**
 * Builds the complete, high-fidelity Stocker3D asset hierarchy.
 * Faithfully matches the real cleanroom stocker from reference Image 1 & 2:
 * - White cleanroom plinth base with dark footing trim
 * - Bold black front structural posts & white rear posts
 * - White top canopy roof with brushed aluminum lintels
 * - Highly transparent cleanroom glass enclosure (see-through front, sides, back)
 * - 2 columns of white wafer carrier shelf tiers (6 tiers) with central spine
 * - Digital OLED status HUD display badge ("0/28") on the front glass
 * - Floor hazard guide slider runner along the bottom-right front
 * - Vertical crane hoist mast column extending to overhead track level
 */
function buildStocker3DModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'Stocker3DTemplate';

  const { w: width, h: height, d: depth } = STOCKER_TEMPLATE_DIMS;
  const baseH = height * 0.08;
  const capH = height * 0.08;
  const pillarBottom = baseH;
  const pillarTop = height - capH;
  const colH = pillarTop - pillarBottom;
  const colY = (pillarTop + pillarBottom) * 0.5;
  const frameT = width * 0.035;
  const colW = frameT * 1.55;
  const hx = width * 0.46;
  const hz = depth * 0.46;

  // Premium Industrial PBR Materials
  const whiteCleanroom = getIndustrialWhiteMaterial();
  const darkFooting = getIndustrialDarkChassisMaterial();
  const darkPostMetal = getSharedStandardMaterial({
    color: '#0F172A',
    metalness: 0.85,
    roughness: 0.22,
    envMapIntensity: 1.4,
  });
  const brushedMetal = getBrushedAluminumMaterial();
  const cleanGlass = getCleanroomGlassMaterial('#BAE6FD', 0.22);
  const whiteShelf = getSharedStandardMaterial({
    color: '#F8FAFC',
    metalness: 0.35,
    roughness: 0.20,
    envMapIntensity: 1.25,
  });
  const oledPlate = getSharedStandardMaterial({
    color: '#060C18',
    metalness: 0.88,
    roughness: 0.18,
    envMapIntensity: 1.5,
  });
  const cyanNeon = getSharedStandardMaterial({
    color: '#00F0FF',
    emissive: '#00F0FF',
    emissiveIntensity: 2.2,
    roughness: 0.1,
    metalness: 0.1,
  });
  const greenPill = getSharedStandardMaterial({
    color: '#10B981',
    emissive: '#10B981',
    emissiveIntensity: 1.8,
    roughness: 0.1,
    metalness: 0.1,
  });
  const hazardYellow = getSharedStandardMaterial({
    color: '#FACC15',
    metalness: 0.2,
    roughness: 0.35,
  });
  const hazardBlack = getSharedStandardMaterial({
    color: '#1E293B',
    metalness: 0.4,
    roughness: 0.4,
  });

  // 1. Lower Base Plinth & Footing Trim (Image 1: White plinth with dark bottom footing)
  const footing = new THREE.Mesh(
    new THREE.BoxGeometry(width * 1.02, baseH * 0.35, depth * 1.0),
    darkFooting
  );
  footing.position.set(0, baseH * 0.175, 0);
  root.add(footing);

  const basePlinth = new THREE.Mesh(
    new THREE.BoxGeometry(width * 1.0, baseH * 0.65, depth * 0.98),
    whiteCleanroom
  );
  basePlinth.position.set(0, baseH * 0.35 + baseH * 0.325, 0);
  root.add(basePlinth);

  const baseTrim = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.98, frameT * 0.45, depth * 0.96),
    brushedMetal
  );
  baseTrim.position.set(0, baseH + frameT * 0.22, 0);
  root.add(baseTrim);

  // 2. Corner Structural Pillars (Image 1: Bold black front posts, clean white rear posts)
  // Front-right (hx, -hz): bold black upright
  const frontRightPillar = new THREE.Mesh(
    new THREE.BoxGeometry(colW * 1.25, colH, colW * 1.25),
    darkPostMetal
  );
  frontRightPillar.position.set(hx, colY, -hz);
  root.add(frontRightPillar);

  // Front-left (-hx, -hz): sleek black upright
  const frontLeftPillar = new THREE.Mesh(
    new THREE.BoxGeometry(colW * 1.05, colH, colW * 1.05),
    darkPostMetal
  );
  frontLeftPillar.position.set(-hx, colY, -hz);
  root.add(frontLeftPillar);

  // Rear pillars (-hx, hz) and (hx, hz): clean white posts
  const rearLeftPillar = new THREE.Mesh(
    new THREE.BoxGeometry(colW, colH, colW),
    whiteCleanroom
  );
  rearLeftPillar.position.set(-hx, colY, hz);
  root.add(rearLeftPillar);

  const rearRightPillar = new THREE.Mesh(
    new THREE.BoxGeometry(colW, colH, colW),
    whiteCleanroom
  );
  rearRightPillar.position.set(hx, colY, hz);
  root.add(rearRightPillar);

  // 3. Top Perimeter Frame & Roof Canopy
  const topCap = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.98, capH * 0.7, depth * 0.97),
    whiteCleanroom
  );
  topCap.position.set(0, pillarTop + capH * 0.35, 0);
  root.add(topCap);

  const topTrim = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.99, frameT * 0.75, depth * 0.98),
    brushedMetal
  );
  topTrim.position.set(0, pillarTop - frameT * 0.375, 0);
  root.add(topTrim);

  // Front dark header lintel
  const frontLintel = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.92, frameT * 0.85, frameT * 0.85),
    darkPostMetal
  );
  frontLintel.position.set(0, pillarTop - frameT * 0.8, -hz);
  root.add(frontLintel);

  // 4. Transparent Cleanroom Glass Panels (Image 1: Highly transparent see-through walls)
  const leftGlass = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, colH * 0.96, depth * 0.90),
    cleanGlass
  );
  leftGlass.position.set(-hx + 0.04, colY, 0);
  root.add(leftGlass);

  const rightGlass = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, colH * 0.96, depth * 0.90),
    cleanGlass
  );
  rightGlass.position.set(hx - 0.04, colY, 0);
  root.add(rightGlass);

  const rearGlass = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.92, colH * 0.96, 0.08),
    cleanGlass
  );
  rearGlass.position.set(0, colY, hz - 0.04);
  root.add(rearGlass);

  const frontGlass = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.92, colH * 0.96, 0.08),
    cleanGlass
  );
  frontGlass.position.set(0, colY, -hz + 0.04);
  root.add(frontGlass);

  // 5. Internal Shelving — 2 COLUMNS OF WHITE SHELVES (Image 1: 2 columns, 6 tiers)
  const centerSpine = new THREE.Mesh(
    new THREE.BoxGeometry(colW * 0.75, colH * 0.96, depth * 0.78),
    brushedMetal
  );
  centerSpine.position.set(0, colY, 0);
  root.add(centerSpine);

  const tierCount = 6;
  const colSpanX = (width * 0.88 - colW) * 0.5;
  const leftColX = -width * 0.44 + colSpanX * 0.5;
  const rightColX = width * 0.44 - colSpanX * 0.5;

  for (let t = 0; t < tierCount; t++) {
    const shelfY = pillarBottom + (colH / (tierCount + 1)) * (t + 1);

    const leftTray = new THREE.Mesh(
      new THREE.BoxGeometry(colSpanX * 0.94, 0.22, depth * 0.74),
      whiteShelf
    );
    leftTray.position.set(leftColX, shelfY, 0);
    root.add(leftTray);

    const rightTray = new THREE.Mesh(
      new THREE.BoxGeometry(colSpanX * 0.94, 0.22, depth * 0.74),
      whiteShelf
    );
    rightTray.position.set(rightColX, shelfY, 0);
    root.add(rightTray);

    [-0.24, 0.24].forEach((rz) => {
      const guideL = new THREE.Mesh(
        new THREE.BoxGeometry(colSpanX * 0.8, 0.12, 0.12),
        brushedMetal
      );
      guideL.position.set(leftColX, shelfY + 0.14, rz * depth);
      root.add(guideL);

      const guideR = new THREE.Mesh(
        new THREE.BoxGeometry(colSpanX * 0.8, 0.12, 0.12),
        brushedMetal
      );
      guideR.position.set(rightColX, shelfY + 0.14, rz * depth);
      root.add(guideR);
    });
  }

  // 6. Front Digital OLED / HUD Status Display Badge ("0/28" as shown in Image 1)
  const badgeW = width * 0.38;
  const badgeH = height * 0.16;
  const badgeX = -width * 0.08;
  const badgeY = colY * 0.92;
  const badgeZ = -hz - 0.08;

  const oledFace = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW, badgeH, 0.08),
    oledPlate
  );
  oledFace.position.set(badgeX, badgeY, badgeZ);
  root.add(oledFace);

  const oledBorder = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW + 0.25, badgeH + 0.25, 0.04),
    cyanNeon
  );
  oledBorder.position.set(badgeX, badgeY, badgeZ - 0.02);
  root.add(oledBorder);

  const statusPill = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW * 0.36, badgeH * 0.22, 0.05),
    greenPill
  );
  statusPill.position.set(badgeX - badgeW * 0.26, badgeY + badgeH * 0.26, badgeZ - 0.05);
  root.add(statusPill);

  const cyanDivider = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW * 0.86, 0.08, 0.04),
    cyanNeon
  );
  cyanDivider.position.set(badgeX, badgeY + badgeH * 0.06, badgeZ - 0.05);
  root.add(cyanDivider);

  const countBar = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW * 0.32, badgeH * 0.32, 0.05),
    cyanNeon
  );
  countBar.position.set(badgeX - badgeW * 0.18, badgeY - badgeH * 0.18, badgeZ - 0.05);
  root.add(countBar);

  const capacityBar = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW * 0.36, badgeH * 0.28, 0.05),
    whiteShelf
  );
  capacityBar.position.set(badgeX + badgeW * 0.20, badgeY - badgeH * 0.18, badgeZ - 0.05);
  root.add(capacityBar);

  const oledTex = getStockerOledTexture();
  if (oledTex) {
    const oledTexMat = getSharedStandardMaterial({
      color: '#FFFFFF',
      map: oledTex,
      emissive: '#FFFFFF',
      emissiveIntensity: 0.95,
      toneMapped: false,
    });
    const oledTexPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(badgeW * 0.96, badgeH * 0.94),
      oledTexMat
    );
    oledTexPlane.position.set(badgeX, badgeY, badgeZ - 0.06);
    oledTexPlane.rotation.y = Math.PI;
    root.add(oledTexPlane);
  }

  // 7. Floor Hazard Slider Guide Runner (Image 1: bottom-right runner with hazard stripes)
  const runnerW = width * 0.18;
  const runnerL = depth * 0.92;
  const runnerX = hx * 0.72;
  const runnerY = 0.08;
  const runnerZ = -hz * 0.15;

  const runnerPlate = new THREE.Mesh(
    new THREE.BoxGeometry(runnerW, 0.14, runnerL),
    brushedMetal
  );
  runnerPlate.position.set(runnerX, runnerY, runnerZ);
  root.add(runnerPlate);

  const stripeCount = 6;
  const stripeStep = runnerL / stripeCount;
  for (let s = 0; s < stripeCount; s++) {
    const isYellow = s % 2 === 0;
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(runnerW * 0.88, 0.04, stripeStep * 0.85),
      isYellow ? hazardYellow : hazardBlack
    );
    stripe.position.set(runnerX, runnerY + 0.09, runnerZ - runnerL * 0.5 + stripeStep * (s + 0.5));
    root.add(stripe);
  }

  [-runnerW * 0.48, runnerW * 0.48].forEach((rx) => {
    const rail = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.28, runnerL),
      darkPostMetal
    );
    rail.position.set(runnerX + rx, runnerY + 0.14, runnerZ);
    root.add(rail);
  });

  // 8. Vertical Crane Hoist Mast Column (Image 1: extends up to overhead track)
  const mastH = 14.0;
  const mastW = width * 0.22;
  const mastD = depth * 0.32;
  const mastY = height + mastH * 0.5;

  const hoistMast = new THREE.Mesh(
    new THREE.BoxGeometry(mastW, mastH, mastD),
    whiteCleanroom
  );
  hoistMast.position.set(0, mastY, hz * 0.2);
  root.add(hoistMast);

  [-mastW * 0.42, mastW * 0.42].forEach((mx) => {
    const rail = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.15, mastH, 16),
      brushedMetal
    );
    rail.position.set(mx, mastY, hz * 0.2);
    root.add(rail);
  });

  const topDock = new THREE.Mesh(
    new THREE.BoxGeometry(mastW * 1.15, 0.65, mastD * 1.15),
    darkPostMetal
  );
  topDock.position.set(0, height + mastH - 0.32, hz * 0.2);
  root.add(topDock);

  return root;
}

/**
 * Builds the complete, high-fidelity CircleStocker rotary asset hierarchy.
 */
function buildCircleStockerModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'CircleStockerTemplate';

  const { w: width, h: height } = CIRCLE_STOCKER_TEMPLATE_DIMS;
  const radius = width * 0.5;
  const baseH = height * 0.08;
  const capH = height * 0.08;
  const colH = height - baseH - capH;
  const centerY = baseH + colH * 0.5;

  const baseMetal = getIndustrialDarkChassisMaterial();
  const chromeRing = getPolishedChromeMaterial();
  const rotaryGlass = getCleanroomGlassMaterial('#0284C7', 0.82);
  const shelfRing = getSharedStandardMaterial({
    color: '#94A3B8',
    metalness: 0.82,
    roughness: 0.24,
    envMapIntensity: 1.3,
  });
  const centerMast = getBrushedAluminumMaterial();

  // 1. Circular Bottom Base Plate & Beveled Rim
  const bottomDisc = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius * 1.02, baseH, 48),
    baseMetal
  );
  bottomDisc.position.set(0, baseH * 0.5, 0);
  root.add(bottomDisc);

  const bottomRing = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 1.03, radius * 1.03, 0.3, 48),
    chromeRing
  );
  bottomRing.position.set(0, baseH + 0.15, 0);
  root.add(bottomRing);

  // 2. Top Circular Canopy
  const topDisc = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.98, radius, capH, 48),
    baseMetal
  );
  topDisc.position.set(0, height - capH * 0.5, 0);
  root.add(topDisc);

  // 3. Central Rotary Robot Mast
  const mast = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.12, radius * 0.12, colH, 24),
    centerMast
  );
  mast.position.set(0, centerY, 0);
  root.add(mast);

  // 4. Cylindrical Curved Acrylic Enclosure
  const cylinderGlass = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.97, radius * 0.97, colH * 0.96, 48, 1, true),
    rotaryGlass
  );
  cylinderGlass.position.set(0, centerY, 0);
  root.add(cylinderGlass);

  // 5. Annular Rotary Storage Shelf Rings
  const tierCount = 5;
  for (let t = 0; t < tierCount; t++) {
    const tierY = baseH + (colH / (tierCount + 1)) * (t + 1);
    const ring = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.92, radius * 0.92, 0.18, 48),
      shelfRing
    );
    ring.position.set(0, tierY, 0);
    root.add(ring);
  }

  // 6. Perimeter Structural Columns (8 perimeter posts)
  const postCount = 8;
  for (let i = 0; i < postCount; i++) {
    const angle = (i / postCount) * Math.PI * 2;
    const px = Math.cos(angle) * (radius * 0.98);
    const pz = Math.sin(angle) * (radius * 0.98);
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.2, colH, 16),
      chromeRing
    );
    post.position.set(px, centerY, pz);
    root.add(post);
  }

  // 7. Front Wafer Transfer Port
  const port = new THREE.Mesh(
    new THREE.BoxGeometry(radius * 0.7, 1.4, radius * 0.25),
    baseMetal
  );
  port.position.set(0, baseH + 1.2, -radius * 1.02);
  root.add(port);

  return root;
}

// ============================================================================
// SUB-MESH REGISTRATION
// ============================================================================

function getOrRegisterParts(typeKey: string, builder: () => THREE.Group): SubMeshPart[] {
  let parts = instancedAssetManager.getAssetParts(typeKey);
  if (!parts) {
    const model = builder();
    parts = instancedAssetManager.registerAsset(typeKey, model);
  }
  return parts;
}

export function getOrRegisterStockerParts(typeKey: 'Stocker3D' | 'CircleStocker'): SubMeshPart[] {
  return getAmhsKitTemplateParts(typeKey, () =>
    getOrRegisterParts(
      typeKey,
      typeKey === 'Stocker3D' ? buildStocker3DModel : buildCircleStockerModel
    )
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export type InstancedStockerProps = {
  descriptors: EquipmentRenderDescriptor[];
  excludeIds?: ReadonlySet<string>;
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
  onSelect?: (id: string) => void;
};

export const InstancedStocker: React.FC<InstancedStockerProps> = React.memo(
  ({ descriptors, excludeIds, layoutMeta, onSelect }) => {
    const layoutElements = useAppSelector((s) => s.layout.elements);
    const kitRevision = useAmhsKitTemplateRevision();

    const stocker3DParts = useMemo(() => getOrRegisterStockerParts('Stocker3D'), [kitRevision]);
    const circleStockerParts = useMemo(
      () => getOrRegisterStockerParts('CircleStocker'),
      [kitRevision]
    );

    // Build instance transforms
    const { stocker3DInstances, circleStockerInstances } = useMemo(() => {
      const standard: InstanceTransform[] = [];
      const circle: InstanceTransform[] = [];

      for (const d of descriptors) {
        if (d.kind !== 'stocker') continue;
        if (excludeIds?.has(d.id)) continue;

        const meta = layoutMeta?.[d.id] ?? layoutElements[d.id];
        const cp = meta?.customProperties;
        const isCircle =
          Boolean(cp?.outsideStocker) ||
          String(cp?.equipmentType || '').includes('CIRCLE') ||
          String(meta?.metadata?.name || '').includes('ROTARY');

        const transform = getEquipmentKitWorldTransform(d);
        const box = resolveKitBox(d);

        if (isCircle) {
          circle.push({
            id: d.id,
            position: [transform.x, transform.y, transform.z],
            rotation: [0, transform.rotationY, 0],
            scale: [
              box.w / CIRCLE_STOCKER_TEMPLATE_DIMS.w,
              box.h / CIRCLE_STOCKER_TEMPLATE_DIMS.h,
              box.d / CIRCLE_STOCKER_TEMPLATE_DIMS.d,
            ],
          });
        } else {
          standard.push({
            id: d.id,
            position: [transform.x, transform.y, transform.z],
            rotation: [0, transform.rotationY, 0],
            scale: [
              box.w / STOCKER_TEMPLATE_DIMS.w,
              box.h / STOCKER_TEMPLATE_DIMS.h,
              box.d / STOCKER_TEMPLATE_DIMS.d,
            ],
          });
        }
      }

      return {
        stocker3DInstances: standard,
        circleStockerInstances: circle,
      };
    }, [descriptors, excludeIds, layoutMeta, layoutElements]);

    if (stocker3DInstances.length === 0 && circleStockerInstances.length === 0) {
      return null;
    }

    return (
      <group name="InstancedStocker">
        {stocker3DInstances.length > 0 && (
          <InstancedMultiMeshGroup
            layerId="stocker-standard"
            parts={stocker3DParts}
            instances={stocker3DInstances}
            onSelect={onSelect}
          />
        )}
        {circleStockerInstances.length > 0 && (
          <InstancedMultiMeshGroup
            layerId="stocker-circle"
            parts={circleStockerParts}
            instances={circleStockerInstances}
            onSelect={onSelect}
          />
        )}
      </group>
    );
  }
);

InstancedStocker.displayName = 'InstancedStocker';
export default InstancedStocker;
